/**
 * useFaqPageJsonLd — injects a single FAQPage JSON-LD <script> block
 * scoped by ``nodeId`` so multiple pages can coexist without clobbering
 * each other.
 *
 * Google surfaces matching answers as expandable rich-snippet
 * accordions directly in SERPs, which materially lifts click-through
 * on long-tail queries (e.g., "do I need Medigap if I have Medicare").
 *
 * The hook removes its own <script> on unmount so SPA navigation
 * doesn't leak stale schema into other pages.
 */
import { useEffect } from "react";

export default function useFaqPageJsonLd(faqs, { nodeId } = {}) {
    useEffect(() => {
        const id = nodeId || "ldjson-faqpage";
        const list = Array.isArray(faqs) ? faqs : [];
        if (!list.length) {
            const old = document.getElementById(id);
            if (old) old.remove();
            return;
        }
        const schema = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: list.map((f) => ({
                "@type": "Question",
                name: f.question,
                acceptedAnswer: {
                    "@type": "Answer",
                    // schema.org's Answer.text is plain prose; Google
                    // strips HTML tags for ranking regardless.
                    text: f.answer,
                },
            })),
        };
        let node = document.getElementById(id);
        if (!node) {
            node = document.createElement("script");
            node.type = "application/ld+json";
            node.id = id;
            document.head.appendChild(node);
        }
        node.textContent = JSON.stringify(schema);
        return () => {
            const cleanup = document.getElementById(id);
            if (cleanup) cleanup.remove();
        };
    }, [faqs, nodeId]);
}
